<template>
  <div class="relative flex min-h-screen min-h-[100dvh] flex-col bg-gradient-to-br from-primary-50 to-primary-100 dark:from-neutral-900 dark:to-neutral-800">
    <!-- Background Pattern -->
    <div class="pointer-events-none absolute inset-0 bg-grid-pattern opacity-5" aria-hidden="true"></div>
    
    <div class="relative flex flex-1 flex-col lg:flex-row">
      <!-- Left Side - Branding -->
      <div class="hidden lg:flex lg:flex-1 lg:flex-col lg:justify-center lg:px-8 xl:px-12 2xl:px-16">
        <div class="mx-auto max-w-md xl:max-w-lg">
          <div class="text-center">
            <h1 class="text-3xl xl:text-4xl font-bold tracking-tight text-primary-900 dark:text-white">
              {{ panelName }}
            </h1>
            <p class="mt-4 text-base xl:text-lg text-neutral-600 dark:text-neutral-300 leading-relaxed">
              {{ $t('auth.systemDescription') }}
            </p>
          </div>
          
          <!-- Features List -->
          <div class="mt-8 xl:mt-12 space-y-5 xl:space-y-6">
            <div class="flex items-start">
              <div class="flex-shrink-0">
                <div class="flex h-9 w-9 xl:h-10 xl:w-10 items-center justify-center rounded-lg bg-primary-500 shadow-lg">
                  <ShieldCheckIcon class="h-5 w-5 xl:h-6 xl:w-6 text-white" />
                </div>
              </div>
              <div class="ml-4">
                <h3 class="text-base xl:text-lg font-semibold text-neutral-900 dark:text-white">
                  Segurança Avançada
                </h3>
                <p class="text-sm xl:text-base text-neutral-600 dark:text-neutral-300 leading-relaxed">
                  Criptografia e autenticação robustas
                </p>
              </div>
            </div>
            
            <div class="flex items-start">
              <div class="flex-shrink-0">
                <div class="flex h-9 w-9 xl:h-10 xl:w-10 items-center justify-center rounded-lg bg-success-500 shadow-lg">
                  <ChartBarIcon class="h-5 w-5 xl:h-6 xl:w-6 text-white" />
                </div>
              </div>
              <div class="ml-4">
                <h3 class="text-base xl:text-lg font-semibold text-neutral-900 dark:text-white">
                  Dashboard Completo
                </h3>
                <p class="text-sm xl:text-base text-neutral-600 dark:text-neutral-300 leading-relaxed">
                  Monitore tudo em tempo real
                </p>
              </div>
            </div>
            
            <div class="flex items-start">
              <div class="flex-shrink-0">
                <div class="flex h-9 w-9 xl:h-10 xl:w-10 items-center justify-center rounded-lg bg-warning-500 shadow-lg">
                  <CogIcon class="h-5 w-5 xl:h-6 xl:w-6 text-white" />
                </div>
              </div>
              <div class="ml-4">
                <h3 class="text-base xl:text-lg font-semibold text-neutral-900 dark:text-white">
                  Fácil Configuração
                </h3>
                <p class="text-sm xl:text-base text-neutral-600 dark:text-neutral-300 leading-relaxed">
                  Interface intuitiva e moderna
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Right Side - Auth Form -->
      <div class="flex flex-1 flex-col justify-center px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:flex-none lg:w-96 xl:w-[28rem]">
        <div class="mx-auto w-full max-w-sm sm:max-w-md lg:max-w-sm">
          <!-- Mobile Logo -->
          <div class="lg:hidden text-center mb-6 sm:mb-8">
            <div class="flex justify-center mb-4">
              <img
                :src="loginLogoUrl"
                :alt="panelName"
                class="h-16 sm:h-20 w-auto object-contain transition-opacity duration-300"
                loading="eager"
                @error="handleLogoError"
              />
            </div>
            <p class="mt-2 text-sm sm:text-base text-neutral-600 dark:text-neutral-300">
              Sistema de gerenciamento moderno
            </p>
          </div>
          
          <!-- Formulário de Login Melhorado -->
          <div class="text-center mb-6 sm:mb-8">
            <!-- Logo Desktop Only (mobile já tem logo acima) -->
            <div class="hidden lg:flex justify-center mb-4">
              <img
                :src="loginLogoUrl"
                :alt="panelName"
                class="h-20 sm:h-24 w-auto object-contain transition-opacity duration-300"
                loading="eager"
                @error="handleLogoError"
              />
            </div>
            <h2 class="text-2xl sm:text-3xl font-bold leading-tight tracking-tight text-neutral-900 dark:text-white mb-2">
              {{ $t('auth.loginTitle') }}
            </h2>
            <p class="text-sm sm:text-base leading-relaxed text-neutral-600 dark:text-neutral-300">
              {{ $t('auth.loginSubtitle') }}
            </p>
          </div>

          <form @submit.prevent="handleLogin" class="space-y-6">
            <div class="space-y-2">
              <label for="login" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {{ $t('auth.loginIdentifier') }}
              </label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg class="h-5 w-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <input
                  id="login"
                  v-model="form.login"
                  name="login"
                  type="text"
                  required
                  autocomplete="username"
                  class="block w-full pl-10 pr-3 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 text-base"
                  :placeholder="$t('auth.loginIdentifierPlaceholder')"
                />
              </div>
            </div>

            <!-- Campo Senha -->
            <div class="space-y-2">
              <label for="password" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {{ $t('auth.password') }}
              </label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg class="h-5 w-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  id="password"
                  v-model="form.password"
                  name="password"
                  :type="showPassword ? 'text' : 'password'"
                  required
                  autocomplete="current-password"
                  class="block w-full pl-10 pr-12 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 text-base"
                  :placeholder="$t('auth.passwordPlaceholder')"
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  class="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors duration-200"
                >
                  <EyeIcon v-if="!showPassword" class="h-5 w-5" />
                  <EyeSlashIcon v-else class="h-5 w-5" />
                </button>
              </div>
            </div>

            <!-- Botão Login -->
            <div class="pt-4">
              <button
                type="submit"
                :disabled="loading"
                class="w-full flex justify-center items-center py-3.5 px-4 border border-transparent rounded-lg shadow-sm text-base font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
              >
                <span v-if="loading" class="flex items-center">
                  <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {{ $t('auth.loggingIn') }}
                </span>
                <span v-else class="flex items-center">
                  <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                  </svg>
                  {{ $t('auth.loginButton') }}
                </span>
              </button>
            </div>

            <!-- Mensagem de Erro -->
            <div v-if="error" class="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 animate-fade-in">
              <div class="flex items-start">
                <svg class="h-5 w-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div class="flex-1">
                  <h4 class="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">
                    {{ $t('auth.loginError') }}
                  </h4>
                  <p class="text-sm text-red-700 dark:text-red-300 leading-relaxed">
                    {{ error }}
                  </p>
                  <div class="mt-3 flex items-center space-x-2">
                    <button
                      @click="error = ''"
                      class="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline focus:outline-none"
                    >
                      {{ $t('auth.tryAgain') }}
                    </button>
                    <span class="text-xs text-red-500 dark:text-red-400">•</span>
                    <span class="text-xs text-red-500 dark:text-red-400">
                      {{ $t('auth.checkCredentials') }}
                    </span>
                  </div>
                </div>
                <button
                  @click="error = ''"
                  class="text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors"
                >
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Links Adicionais -->
            <div class="text-center pt-4">
              <p class="text-xs text-neutral-500 dark:text-neutral-400">
                Sistema seguro • Criptografia SSL • Suporte 24/7
              </p>
            </div>
          </form>

          <!-- Filhos do router (/auth/login, etc.) — o formulário actual está neste layout -->
          <router-view />
        </div>
      </div>
    </div>

    <PanelFooter pin />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useBranding } from '@/composables/useBranding'
import { ShieldCheckIcon, ChartBarIcon, CogIcon, EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/outline'
import PanelFooter from '@/components/layout/PanelFooter.vue'

const form = reactive({
  login: '',
  password: ''
})

// State
const loading = ref(false)
const error = ref('')
const showPassword = ref(false)

// Router and stores
const router = useRouter()
const authStore = useAuthStore()
const { t } = useI18n()

// Branding (instantâneo)
const { getLoginLogo, getPanelName, loadBranding, getImageUrl } = useBranding()

// Logo (instantâneo - retorna padrão imediatamente, depois substitui por customizado)
const loginLogoUrl = computed(() => {
  const logoPath = getLoginLogo.value
  return getImageUrl(logoPath)
})

const panelName = computed(() => getPanelName.value)

// Handle de erro do logo (fallback para logo padrão)
const logoErrorHandled = ref(false)
const handleLogoError = (event) => {
  if (logoErrorHandled.value) return // Previne loop infinito
  logoErrorHandled.value = true
  event.target.src = '/login.png'
}

// Carregar branding em background (não bloqueia renderização)
onMounted(() => {
  loadBranding().catch(err => console.warn('Erro ao carregar branding:', err))
})

// Handle login
const handleLogin = async () => {
  loading.value = true
  error.value = ''
  
  try {
    const result = await authStore.login({
      login: form.login.trim(),
      password: form.password
    })
    
    // Redirecionar para dashboard unificada (todos os roles)
    router.push('/')
  } catch (err) {
    console.error('❌ Erro no login:', err)
    console.error('❌ Tipo de erro:', typeof err)
    console.error('❌ Mensagem:', err.message)
    console.error('❌ Stack:', err.stack)
    
    // Exibe a mensagem de erro de forma clara
    if (err.message) {
      error.value = err.message
    } else if (typeof err === 'string') {
      error.value = err
    } else if (err.response?.data?.message) {
      error.value = err.response.data.message
    } else {
      error.value = t('viewMessages.auth.loginFallbackError')
    }
    
    // Limpa apenas a senha, mantém o usuário/email
    form.password = ''
    
    // Foca no campo de senha para facilitar nova tentativa
    setTimeout(() => {
      const passwordField = document.getElementById('password')
      if (passwordField) {
        passwordField.focus()
      }
    }, 100)
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.bg-grid-pattern {
  background-image: 
    linear-gradient(rgba(0, 0, 0, 0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 0, 0, 0.1) 1px, transparent 1px);
  background-size: 20px 20px;
}

.dark .bg-grid-pattern {
  background-image: 
    linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
}

/* Animação para mensagem de erro */
@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fade-in 0.3s ease-out;
}
</style>